package com.siriusxm.pia.components

import androidx.compose.runtime.*
import com.siriusxm.cmc.utils.Navigator
import com.siriusxm.pia.SXMUI
import com.siriusxm.pia.components.TabStyles.tabContainer
import com.siriusxm.pia.components.TabStyles.tabTitle
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

object TabStyles : StyleSheet() {
    val tabContainer by style {
        border(0.px, LineStyle.Solid, SXMUI.tabsBorder.value())
        borderWidth(0.px, 0.px, 1.px, 0.px)
        margin(0.px, 10.px)

        child(self, type("div")).style {
            lastChild.style {
                type("span").style {
                    borderWidth(0.px)
                }
            }
        }
    }

    val tabNarrowPopup by style {
        display(DisplayStyle.Block)
        padding(0.px, 5.px)
        marginBottom(5.px)
        whenWiderThanPhone {
            display(DisplayStyle.None)
        }
        type("select") style {
            width(100.percent)
        }
    }

    val tabRow by style {
        display(DisplayStyle.None)
        whenWiderThanPhone {
            display(DisplayStyle.Block)
        }
    }

    val tabTitle by style {
        margin((0.5).em, 0.px)
        padding((0.5).em, (1.5).em)
        fontWeight(700)
        display(DisplayStyle.InlineBlock)

        border(0.px, LineStyle.Solid, SXMUI.tabsBorder.value())
        borderWidth(0.px, 1.px, 0.px, 0.px)

    }

    val tabSelected by style {
        border(0.px, LineStyle.Solid, SXMUI.brandColor.value())
        borderWidth(0.px, 0.px, 2.px, 0.px)

        className(tabTitle).style {
            color(SXMUI.brandColor.value())
        }
    }

    val tab by style {
        display(DisplayStyle.InlineBlock)
        cursor("pointer")

        self + hover style {
            className(tabTitle).style {
                color(SXMUI.brandColor.value())
            }
        }
    }

    val tabContent by style {
        marginTop(10.px)
    }
}


interface TabViewProperties {
    var selection: String?
    var allowNoSelect: Boolean
    var padding: Boolean

    /**
     * When true, the tab view fills its parent container height exactly using flex layout.
     * The tab bar remains fixed at the top while the selected tab's content fills the
     * remaining space, allowing child components to manage their own scrolling.
     * Use in conjunction with ServiceContentViewConfiguration.fixedHeight for
     * full-height layouts containing tabs.
     */
    var fixedHeight: Boolean

    fun tab(title: String, id: String = title.lowercase(), content: @Composable () -> Unit)

    /**
     * Provide a function to be called when any tab is clicked. The callback will receive the id
     * of the tab. If it returns false, the tab switch will not take place.
     */
    fun onSelect(cb: (String) -> Boolean)

    /**
     * Enables tab navigation by navigating to a URL based on the ID of the tab selected.
     * The caller would still need to set the selection based on that path.
     */
    fun tabNavigation(navigator: Navigator, baseUrl: String, selection: String?)
}

private class TabConfig(
    val title: String,
    val id: String,
    val content: @Composable () -> Unit
)

private class TabViewConfigurator : TabViewProperties {
    override var selection: String? = null
    val tabs = mutableListOf<TabConfig>()
    var selectCb: ((String) -> Boolean)? = null
    override var allowNoSelect: Boolean = false
    override var padding: Boolean = true
    override var fixedHeight: Boolean = false

    override fun tabNavigation(navigator: Navigator, baseUrl: String, selection: String?) {
        selectCb = {
            navigator.navigate("$baseUrl/${it}")
            false
        }
        this.selection = selection
    }

    override fun tab(title: String, id: String, content: @Composable () -> Unit) {
        tabs += TabConfig(title, id, content)
        if (selection == null && !allowNoSelect) {
            selection = id
        }
    }

    override fun onSelect(cb: (String) -> Boolean) {
        selectCb = cb
    }
}

/**
 * A tabbed view component that renders a row of selectable tabs with corresponding content.
 * The tab bar stays fixed while switching between tab contents. Supports both controlled
 * (via onSelect/tabNavigation) and uncontrolled (internal state) selection modes.
 * When [TabViewProperties.fixedHeight] is true, the tab view fills its parent container
 * using flex column layout, with the tab bar fixed at the top and the selected tab's
 * content filling the remaining space.
 */
@Composable
fun tabView(config: TabViewProperties.() -> Unit) {
    Style(TabStyles)

    val data = TabViewConfigurator().apply(config)
    var selectedRemembered by remember {
        mutableStateOf(data.selection)
    }

    val selected = if (data.selectCb != null) data.selection else selectedRemembered

    // When fixedHeight is enabled, wrap everything in a flex column so the tab content
    // can fill the remaining space below the tab bar.
    val wrapper: @Composable (@Composable () -> Unit) -> Unit = if (data.fixedHeight) {
        { inner ->
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    height(100.percent)
                    minHeight(0.px)
                }
            }) {
                inner()
            }
        }
    } else {
        { inner -> inner() }
    }

    wrapper {
        Div({
            classes(tabContainer)

            style {
                if (data.padding) {
                    paddingTop(5.px)
                }
                if (data.fixedHeight) {
                    flexShrink(0)
                }
            }
        }) {
            Div({
                classes(TabStyles.tabNarrowPopup)
            }) {
                simpleSelectNullable(
                    data.tabs.find { it.id == selected },
                    data.tabs.map {
                        it.title to it
                    }) { newSelection ->
                    if (newSelection != null) {
                        if (data.selectCb != null) {
                            data.selectCb?.invoke(newSelection.id)
                        } else {
                            selectedRemembered = newSelection.id
                        }
                    }
                }
            }

            Div({
                classes(TabStyles.tabRow)
            }) {
                data.tabs.forEach { tab ->
                    Div({
                        classes(TabStyles.tab)
                        if (tab.id == selected) {
                            classes(TabStyles.tabSelected)
                        }

                        onClick {
                            if (data.selectCb != null) {
                                data.selectCb?.invoke(tab.id)
                            } else {
                                selectedRemembered = tab.id
                            }
                        }
                    }) {
                        Span({
                            classes(tabTitle)

                        }) {
                            Text(tab.title)
                        }
                    }
                }
            }
        }

        data.tabs.find { it.id == selected }?.let { selectedTab ->
            Div({
                if (data.fixedHeight) {
                    style {
                        flex(1)
                        minHeight(0.px)
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                    }
                }
            }) {
                selectedTab.content()
            }
        }
    }
}

@Composable
fun tabContent(content: @Composable () -> Unit) {
    Div({
        classes(TabStyles.tabContent)
    }) {
        content()
    }
}


/**
 * Displays a loading spinner that is designed to be displayed in an empty tab.
 */
@Composable
fun tabLoading(delay: Long = 200) {
    Div({ style { padding(10.px) } }) {
        spinner(size = Size.NORMAL, delay = delay)
    }
}